/**
 *
 * @component 个人中心/我的方案/项目管理/协作项目详情/施工进度计划tab/进度详情抽屉/增项记录
 *
 **/

import React, { memo } from 'react';
import {
    Space,
    Radio,
    message,
    Modal,
    Input,
    Form,
    Button,
    Checkbox,
} from 'antd';
import moment from 'moment';
import BaseModal from '&/components/BaseModal';
import {
    getReasons,
    addAdditional,
    putAdditional,
    getIncreaseList,
    deleteAdditional,
} from '@/api/personCenter/projectSpace1.4';
import BaseListWrap from '&/components/BaseListWrap';
import './AdditionalRecord.scss';

const { Item } = Form;

function AdditionalRecord(props) {
    useEffect(() => {
        getList();
        queryReasons();
    }, []);

    const { planId, projectId, perm, needRefresh } = props;
    const [list, setList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const cacheLen = useRef(-1); // 列表长度缓存，观察是否有变化

    function getList() {
        setIsLoading(true);
        getIncreaseList({ projectId, planId })
            .then(({ data }) => {
                data.reason = JSON.parse(data.reason || '[]');
                setList(data);
                if (cacheLen.current === -1) {
                    // 第一次记录
                    cacheLen.current = data.length;
                }

                if (
                    (cacheLen.current === 0 && data.length > 0) || // 从无到有
                    (cacheLen.current > 0 && data.length === 0) // 从有到无
                ) {
                    // 触发甘特图刷新
                    needRefresh();
                }
            })
            .finally(() => {
                setIsLoading(false);
            });
    }
    const [reasons, setReasons] = useState([]);

    function queryReasons() {
        getReasons().then(({ data }) => {
            setReasons(Object.values(data.reason));
        });
    }

    //  正负数 保留两位小数
    const parseNumber = num => {
        if (isNaN(num) && !/^-$/.test(num)) {
            num = '';
        }
        if (!/^[+-]?\d*\.{0,1}\d{0,1}$/.test(num)) {
            num = num.replace(/\.\d{2,}$/, num.substr(num.indexOf('.'), 3));
        }
        if (num > 99999999.99) {
            num = 99999999.99;
        }
        if (num < -99999999.99) {
            num = -99999999.99;
        }
        return num;
    };

    const [formRef] = Form.useForm();
    const [visible, setVisible] = useState(false);
    const EDIT = 1,
        ADD = 0;
    const formType = useRef(ADD);

    const openModal = record => {
        if (record) {
            formRef.setFieldsValue({
                price: record.price,
                remark: record.remark,
                reason: record.reason,
                isOwnerAffirm: record.isOwnerAffirm - 0,
                entryId: record.id,
            });
        }
        formType.current = record ? EDIT : ADD;
        setVisible(true);
    };

    const onCancel = () => {
        formRef.resetFields();
        setVisible(false);
    };

    const [sendLoading, setSendLoading] = useState(false);

    const onSubmit = payload => {
        setSendLoading(true);
        const submitFn = [addAdditional, putAdditional][formType.current];
        submitFn(payload)
            .then(({ msg }) => {
                message.success(msg);
                getList();
                onCancel();
            })
            .finally(() => {
                setSendLoading(false);
            });
    };

    const onDelete = entryId => {
        const modal = Modal.confirm({
            content: `${intl.get('templateSettings.areYouWantToDelete')}?`,
            okText: intl.get('base.delete'),
            cancelText: intl.get('base.cancel'),
            okButtonProps: { loading: false },
            centered: true,
            onOk: async () => {
                modal.update({ okButtonProps: { loading: true } });
                try {
                    const { msg } = await deleteAdditional({
                        projectId,
                        planId,
                        entryId,
                    });
                    message.success(msg);
                    getList();
                } finally {
                    modal.update({ okButtonProps: { loading: false } });
                }
            },
        });
    };

    const CONFIRM_DICT = [
        intl.get('projectSpace.unneeded'),
        intl.get('projectSpace.needSignature'),
        intl.get('projectSpace.needConfirm'),
    ];

    const calcStatus = ({ status, isOwnerAffirm, signaturePath }) => {
        if (isOwnerAffirm === '0') return intl.get('projectSpace.unneeded');
        if (status === '1') {
            return [
                intl.get('projectSpace.waitSignature'),
                intl.get('projectSpace.unconfirm'),
            ][isOwnerAffirm - 1];
        }
        if (status === '2') {
            return [
                <img className="signature-image" src={signaturePath} />,
                intl.get('projectSpace.confirmed'),
            ][isOwnerAffirm - 1];
        }
        if (status === '3') {
            return intl.get('userCenter.notPass');
        }
    };

    const supportRemove = ({ status, createDate, isOwnerAffirm }) => {
        let bool = false;
        // 待确认待签字允许删除
        if (isOwnerAffirm > 0 && status === '1') {
            bool = true;
        }
        // 无需确认的允许两分钟内删除
        if (isOwnerAffirm === '0') {
            let limit2 = moment().subtract(2, 'm');
            let createTime = moment.unix(createDate);
            let isBefore = limit2.isSameOrBefore(createTime, 'm');
            bool = isBefore;
        }
        return bool;
    };

    return (
        <div className="additional-record">
            <header className="additional-record__header">
                {intl.get('projectSpace.additionalRecord')}
                <div
                    className={`native-link ${
                        perm.includes('entryAdd') ? '' : 'type--disabled'
                    }`}
                    onClick={() => perm.includes('entryAdd') && openModal()}>
                    + {intl.get('projectSpace.additionalAdd')}
                </div>
            </header>

            <BaseListWrap isLoading={isLoading} isEmpty={!!list.length}>
                {list.map(item => {
                    return (
                        <section className="additional-item" key={item.id}>
                            <header className="additional-item__header">
                                <div className="item-label">
                                    {intl.get('projectSpace.expenseLabel')}
                                </div>
                                <div className="item-value">{item.price}</div>
                                <div className="item-status">
                                    <span className="item-label">
                                        {intl.get(
                                            'projectSpace.ownerConfirmLabel'
                                        )}
                                    </span>
                                    {calcStatus(item)}
                                </div>
                                <div className="icons">
                                    {item.isOwnerAffirm > 0 &&
                                        item.status === '1' && (
                                            <i
                                                className={`icon-pencil filled ${
                                                    perm.includes('entryUpdate')
                                                        ? 'pointer'
                                                        : 'type--disabled'
                                                }`}
                                                onClick={() =>
                                                    perm.includes(
                                                        'entryUpdate'
                                                    ) && openModal(item)
                                                }
                                            />
                                        )}
                                    {supportRemove(item) && (
                                        <i
                                            className={`icon-trash filled ${
                                                perm.includes('entryDelete')
                                                    ? 'pointer'
                                                    : 'type--disabled'
                                            }`}
                                            onClick={() =>
                                                perm.includes('entryDelete') &&
                                                onDelete(item.id)
                                            }
                                        />
                                    )}
                                </div>
                            </header>
                            <div className="additional-item__body">
                                <div className="item-label">
                                    {intl.get('projectSpace.reasonLabel')}
                                </div>
                                <div className="item-value">
                                    {item.reason
                                        ?.map((el, idx) => `${idx + 1}、${el}`)
                                        .join('; ')}
                                </div>
                            </div>
                            <div className="additional-item__body">
                                <div className="item-label">
                                    {intl.get('templateSettings.remarkLabel')}
                                </div>
                                <div className="item-value">{item.remark}</div>
                            </div>
                        </section>
                    );
                })}
            </BaseListWrap>

            <BaseModal
                title={
                    [
                        intl.get('projectSpace.additionalAdd'),
                        intl.get('projectSpace.additionalEdit'),
                    ][formType.current]
                }
                width="28.75rem"
                wrapClassName="additional-modal"
                footer={
                    <Space>
                        <Button onClick={onCancel}>
                            {intl.get('base.cancel')}
                        </Button>
                        <Button
                            onClick={formRef.submit}
                            loading={sendLoading}
                            type="primary">
                            {intl.get('base.confirm')}
                        </Button>
                    </Space>
                }
                visible={visible}
                onCancel={onCancel}>
                <Form
                    form={formRef}
                    className="form-group"
                    onFinish={onSubmit}
                    preserve={false}
                    labelCol={{ span: 5 }}>
                    <Item
                        label={intl.get('projectSpace.additionalExpense')}
                        name="price"
                        normalize={parseNumber}
                        rules={[
                            {
                                required: true,
                            },
                        ]}>
                        <Input placeholder="0.00" />
                    </Item>
                    <Item
                        style={{
                            margin: '0',
                        }}
                        name="reason"
                        rules={[
                            {
                                required: true,
                                type: 'array',
                                message: intl.get('projectSpace.checkReason'),
                            },
                        ]}
                        label={intl.get('projectSpace.reason')}>
                        <Checkbox.Group style={{ width: '100%' }}>
                            {reasons.map((e, idx) => {
                                return (
                                    <Checkbox key={idx} value={e}>
                                        {idx + 1}、{e}
                                    </Checkbox>
                                );
                            })}
                        </Checkbox.Group>
                    </Item>

                    <Item name="remark" label={intl.get('base.remark')}>
                        <Input.TextArea
                            allowClear
                            autoSize={{ minRows: 4, maxRows: 4 }}
                        />
                    </Item>
                    <Item
                        label={intl.get('projectSpace.ownerConfirm')}
                        name="isOwnerAffirm"
                        initialValue={0}>
                        <Radio.Group>
                            {CONFIRM_DICT.map((e, i) => (
                                <Radio key={i} value={i}>
                                    {e}
                                </Radio>
                            ))}
                        </Radio.Group>
                    </Item>
                    <Item hidden name="projectId" initialValue={projectId} />
                    <Item hidden name="planId" initialValue={planId}></Item>
                    <Item hidden name="entryId"></Item>
                </Form>
            </BaseModal>
        </div>
    );
}

export default memo(AdditionalRecord);
